import ExcelJS from 'exceljs';

/**
 * FAN SELECTION 섹션을 엑셀 시트에 추가
 * 출력 시트 레이아웃 (output1.xlsx A64:M108 형식 참고):
 * - 가로: Internal S.P (A-C) | External S.P (D-G) | Supply Fan/Return Fan (H-M)
 * - Internal S.P: 라벨(A-B 병합), 값(C)
 * - External S.P: 라벨(D-E 병합), SA값(F), RA값(G)
 * - Supply/Return Fan: 라벨(H-I 병합), 값(J-M 병합)
 * @param worksheet 엑셀 워크시트
 * @param ahu AHU 데이터
 * @param currentRow 시작 행 번호
 * @param STYLES 스타일 객체
 * @returns 다음 행 번호
 */
export const addFanSelection = (
  worksheet: ExcelJS.Worksheet,
  ahu: any,
  currentRow: number,
  STYLES: any
): number => {
  let row: ExcelJS.Row;

  const fanData = ahu.fanSelectionData;
  const supplyFan = fanData?.supplyFan || {};
  const returnFan = fanData?.returnFan || {};

  // 병합된 셀에 스타일 적용하는 헬퍼 함수
  const applyMergedCellStyle = (ws: ExcelJS.Worksheet, range: string, style: any) => {
    const [start, end] = range.split(':');

    if (!end) {
      // 단일 셀인 경우
      ws.getCell(start).style = style;
      return;
    }

    // 범위 파싱 (예: A10:B10)
    const startCol = start.match(/[A-Z]+/)?.[0] || '';
    const startRow = parseInt(start.match(/\d+/)?.[0] || '0');
    const endCol = end.match(/[A-Z]+/)?.[0] || '';
    const endRow = parseInt(end.match(/\d+/)?.[0] || '0');

    // 열 문자를 숫자로 변환
    const colToNum = (col: string): number => {
      let num = 0;
      for (let i = 0; i < col.length; i++) {
        num = num * 26 + (col.charCodeAt(i) - 64);
      }
      return num;
    };

    const startColNum = colToNum(startCol);
    const endColNum = colToNum(endCol);

    // 병합된 모든 셀에 스타일 적용
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startColNum; col <= endColNum; col++) {
        ws.getRow(row).getCell(col).style = style;
      }
    }
  };

  // 값 검증 함수
  const sanitizeCellValue = (value: any): string | number => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && !isNaN(value)) return value;
    return String(value);
  };

  // ============================================
  // FAN SELECTION 제목
  // ============================================
  worksheet.mergeCells(`A${currentRow}:M${currentRow}`);
  const fanTitleCell = worksheet.getCell(`A${currentRow}`);
  fanTitleCell.value = '■ FAN SELECTION';
  fanTitleCell.style = {
    font: { name: '맑은 고딕', size: 11, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' }
  };
  currentRow++;

  // ============================================
  // 헤더 행: Internal S.P | External S.P | Supply Fan
  // ============================================
  const headerRow = currentRow;
  row = worksheet.getRow(headerRow);

  // Internal S.P 헤더 (A-C)
  worksheet.mergeCells(`A${headerRow}:C${headerRow}`);
  row.getCell('A').value = '■ Internal S.P (Pa)';
  applyMergedCellStyle(worksheet, `A${headerRow}:C${headerRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });

  // External S.P 헤더 (D-G)
  worksheet.mergeCells(`D${headerRow}:G${headerRow}`);
  row.getCell('D').value = '■ External S.P (Pa)';
  applyMergedCellStyle(worksheet, `D${headerRow}:G${headerRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });

  // Supply Fan 헤더 (H-M)
  worksheet.mergeCells(`H${headerRow}:M${headerRow}`);
  row.getCell('H').value = '■ Supply Fan';
  applyMergedCellStyle(worksheet, `H${headerRow}:M${headerRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });

  currentRow++;

  // ============================================
  // Internal S.P (Pa) - 좌측 컬럼 (A-C)
  // ============================================
  const internalStartRow = currentRow;
  const internalItems = [
    { label: '냉각 코일', value: fanData?.internalPressureDrop?.coolingCoil || 0 },
    { label: '재열 코일', value: fanData?.internalPressureDrop?.reheatingCoil || 0 },
    { label: '예열 코일', value: fanData?.internalPressureDrop?.preheatingCoil || 0 },
    { label: '가열 코일', value: fanData?.internalPressureDrop?.heatingCoil || 0 },
    { label: '배열회수기', value: fanData?.internalPressureDrop?.heatRecovery || 0 },
    { label: 'Pre Filter', value: fanData?.internalPressureDrop?.preFilter || 0 },
    { label: 'Medium Filter', value: fanData?.internalPressureDrop?.mediumFilter || 0 },
    { label: 'Final Filter', value: fanData?.internalPressureDrop?.finalFilter || 0 },
    { label: 'Plenum/Casing', value: fanData?.internalPressureDrop?.plenumCasing || 0 },
    { label: 'Chamber', value: fanData?.internalPressureDrop?.chamber || 0 },
    { label: 'Volume Damper', value: fanData?.internalPressureDrop?.volumeDamper || 0 }
  ];

  let tempRow = internalStartRow;
  internalItems.forEach(item => {
    row = worksheet.getRow(tempRow);
    // 라벨을 A-B 2열로 병합
    worksheet.mergeCells(`A${tempRow}:B${tempRow}`);
    row.getCell('A').value = item.label;
    applyMergedCellStyle(worksheet, `A${tempRow}:B${tempRow}`, STYLES.TABLE_CELL);
    // 값을 C열에
    row.getCell('C').value = item.value.toFixed(1);
    row.getCell('C').numFmt = '0.0';
    row.getCell('C').style = { ...STYLES.TABLE_CELL, numFmt: '0.0', alignment: { vertical: 'middle', horizontal: 'right' } };
    tempRow++;
  });

  // Safety Factor
  row = worksheet.getRow(tempRow);
  worksheet.mergeCells(`A${tempRow}:B${tempRow}`);
  row.getCell('A').value = `Safety Factor (${fanData?.internalPressureDrop?.safetyFactorPercent || 10}%)`;
  applyMergedCellStyle(worksheet, `A${tempRow}:B${tempRow}`, {
    ...STYLES.TABLE_CELL_BOLD,
    border: {
      top: { style: 'medium', color: { argb: 'FF000000' } },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });
  row.getCell('C').value = (fanData?.internalPressureDrop?.safetyFactorValue || 0).toFixed(1);
  row.getCell('C').numFmt = '0.0';
  row.getCell('C').style = {
    ...STYLES.TABLE_CELL_BOLD,
    numFmt: '0.0',
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'medium', color: { argb: 'FF000000' } },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };
  tempRow++;

  // Sub-Total
  row = worksheet.getRow(tempRow);
  worksheet.mergeCells(`A${tempRow}:B${tempRow}`);
  row.getCell('A').value = 'Sub-Total';
  applyMergedCellStyle(worksheet, `A${tempRow}:B${tempRow}`, STYLES.TABLE_CELL_BOLD);
  row.getCell('C').value = (fanData?.internalPressureDrop?.subTotal || 0).toFixed(1);
  row.getCell('C').numFmt = '0.0';
  row.getCell('C').style = { ...STYLES.TABLE_CELL_BOLD, numFmt: '0.0', alignment: { vertical: 'middle', horizontal: 'right' } };
  tempRow++;

  const internalEndRow = tempRow - 1;

  // ============================================
  // External S.P (Pa) - 중앙 컬럼 (D-G)
  // ============================================
  tempRow = internalStartRow;

  // Duct 헤더
  row = worksheet.getRow(tempRow);
  worksheet.mergeCells(`D${tempRow}:E${tempRow}`);
  row.getCell('D').value = 'Duct';
  applyMergedCellStyle(worksheet, `D${tempRow}:E${tempRow}`, STYLES.TABLE_CELL);
  row.getCell('F').value = '(SA)';
  row.getCell('F').style = STYLES.TABLE_CELL;
  row.getCell('G').value = '(RA)';
  row.getCell('G').style = STYLES.TABLE_CELL;
  tempRow++;

  // Duct 항목들 - Supply와 Return 각각 2행씩
  const sFriction1 = fanData?.externalPressureDrop?.supply?.ductFriction1 || 0;
  const sLength1 = fanData?.externalPressureDrop?.supply?.ductLength1 || 0;
  const sCalc1 = Math.round((sFriction1 * sLength1) * 10) / 10;

  const sFriction2 = fanData?.externalPressureDrop?.supply?.ductFriction2 || 0;
  const sLength2 = fanData?.externalPressureDrop?.supply?.ductLength2 || 0;
  const sCalc2 = Math.round((sFriction2 * sLength2) * 10) / 10;

  const rFriction1 = fanData?.externalPressureDrop?.return?.ductFriction1 || 0;
  const rLength1 = fanData?.externalPressureDrop?.return?.ductLength1 || 0;
  const rCalc1 = Math.round((rFriction1 * rLength1) * 10) / 10;

  const rFriction2 = fanData?.externalPressureDrop?.return?.ductFriction2 || 0;
  const rLength2 = fanData?.externalPressureDrop?.return?.ductLength2 || 0;
  const rCalc2 = Math.round((rFriction2 * rLength2) * 10) / 10;

  const externalItems = [
    {
      label: `S:${sFriction1}Pa/m×${sLength1}m`,
      saValue: sCalc1 !== 0 ? sCalc1.toFixed(1) : '',
      raValue: ''
    },
    {
      label: `S:${sFriction2}Pa/m×${sLength2}m`,
      saValue: sCalc2 !== 0 ? sCalc2.toFixed(1) : '',
      raValue: ''
    },
    {
      label: `R:${rFriction1}Pa/m×${rLength1}m`,
      saValue: '',
      raValue: rCalc1 !== 0 ? rCalc1.toFixed(1) : ''
    },
    {
      label: `R:${rFriction2}Pa/m×${rLength2}m`,
      saValue: '',
      raValue: rCalc2 !== 0 ? rCalc2.toFixed(1) : ''
    },
    {
      label: 'Fittings (50%)',
      saValue: (fanData?.externalPressureDrop?.ductFittings?.saValue || 0).toFixed(1),
      raValue: (fanData?.externalPressureDrop?.ductFittings?.raValue || 0).toFixed(1)
    },
    {
      label: 'Terminal Unit',
      saValue: ((fanData?.externalPressureDrop?.equipment?.terminalUnit?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.terminalUnit.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.terminalUnit?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.terminalUnit.ra).toFixed(1) : '')
    },
    {
      label: 'Pre Heating Coil',
      saValue: ((fanData?.externalPressureDrop?.equipment?.preHeatingCoil?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.preHeatingCoil.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.preHeatingCoil?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.preHeatingCoil.ra).toFixed(1) : '')
    },
    {
      label: 'Re Heating Coil',
      saValue: ((fanData?.externalPressureDrop?.equipment?.reHeatingCoil?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.reHeatingCoil.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.reHeatingCoil?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.reHeatingCoil.ra).toFixed(1) : '')
    },
    {
      label: 'OA Louver',
      saValue: ((fanData?.externalPressureDrop?.equipment?.oaLouver?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.oaLouver.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.oaLouver?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.oaLouver.ra).toFixed(1) : '')
    },
    {
      label: 'EA Louver',
      saValue: ((fanData?.externalPressureDrop?.equipment?.eaLouver?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.eaLouver.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.eaLouver?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.eaLouver.ra).toFixed(1) : '')
    },
    {
      label: 'Diffuser/Grille',
      saValue: ((fanData?.externalPressureDrop?.equipment?.diffuserGrille?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.diffuserGrille.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.diffuserGrille?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.diffuserGrille.ra).toFixed(1) : '')
    },
    {
      label: 'Flexible Duct',
      saValue: ((fanData?.externalPressureDrop?.equipment?.flexibleDuct?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.flexibleDuct.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.flexibleDuct?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.flexibleDuct.ra).toFixed(1) : '')
    },
    {
      label: 'Volume Damper',
      saValue: ((fanData?.externalPressureDrop?.equipment?.volumeDamper?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.volumeDamper.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.volumeDamper?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.volumeDamper.ra).toFixed(1) : '')
    },
    {
      label: 'Fire Damper',
      saValue: ((fanData?.externalPressureDrop?.equipment?.fireDamper?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.fireDamper.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.fireDamper?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.fireDamper.ra).toFixed(1) : '')
    },
    {
      label: 'Sound Attenuator',
      saValue: ((fanData?.externalPressureDrop?.equipment?.soundAttenuator?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.soundAttenuator.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.soundAttenuator?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.soundAttenuator.ra).toFixed(1) : '')
    },
    {
      label: 'RA Plenum',
      saValue: ((fanData?.externalPressureDrop?.equipment?.raPlenum?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.raPlenum.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.raPlenum?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.raPlenum.ra).toFixed(1) : '')
    },
    {
      label: 'Velocity Pressure',
      saValue: ((fanData?.externalPressureDrop?.equipment?.velocityPressure?.sa || 0) !== 0 ? (fanData.externalPressureDrop.equipment.velocityPressure.sa).toFixed(1) : ''),
      raValue: ((fanData?.externalPressureDrop?.equipment?.velocityPressure?.ra || 0) !== 0 ? (fanData.externalPressureDrop.equipment.velocityPressure.ra).toFixed(1) : '')
    }
  ];

  externalItems.forEach(item => {
    row = worksheet.getRow(tempRow);
    // 라벨을 D-E 2열로 병합
    worksheet.mergeCells(`D${tempRow}:E${tempRow}`);
    row.getCell('D').value = item.label;
    applyMergedCellStyle(worksheet, `D${tempRow}:E${tempRow}`, STYLES.TABLE_CELL);
    // SA 값을 F열에
    row.getCell('F').value = item.saValue;
    row.getCell('F').style = { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } };
    // RA 값을 G열에
    row.getCell('G').value = item.raValue;
    row.getCell('G').style = { ...STYLES.TABLE_CELL, alignment: { vertical: 'middle', horizontal: 'right' } };
    tempRow++;
  });

  // Safety Factor
  row = worksheet.getRow(tempRow);
  worksheet.mergeCells(`D${tempRow}:E${tempRow}`);
  row.getCell('D').value = `Safety Factor (${fanData?.externalPressureDrop?.sa?.safetyFactorPercent || 10}%)`;
  applyMergedCellStyle(worksheet, `D${tempRow}:E${tempRow}`, {
    ...STYLES.TABLE_CELL_BOLD,
    border: {
      top: { style: 'medium', color: { argb: 'FF000000' } },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });
  row.getCell('F').value = (fanData?.externalPressureDrop?.sa?.safetyFactorValue || 0).toFixed(1);
  row.getCell('F').style = {
    ...STYLES.TABLE_CELL_BOLD,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'medium', color: { argb: 'FF000000' } },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };
  row.getCell('G').value = (fanData?.externalPressureDrop?.ra?.safetyFactorValue || 0).toFixed(1);
  row.getCell('G').style = {
    ...STYLES.TABLE_CELL_BOLD,
    alignment: { vertical: 'middle', horizontal: 'right' },
    border: {
      top: { style: 'medium', color: { argb: 'FF000000' } },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  };
  tempRow++;

  // Sub-Total
  row = worksheet.getRow(tempRow);
  worksheet.mergeCells(`D${tempRow}:E${tempRow}`);
  row.getCell('D').value = 'Sub-Total';
  applyMergedCellStyle(worksheet, `D${tempRow}:E${tempRow}`, STYLES.TABLE_CELL_BOLD);
  row.getCell('F').value = (fanData?.externalPressureDrop?.sa?.subTotal || 0).toFixed(1);
  row.getCell('F').style = { ...STYLES.TABLE_CELL_BOLD, alignment: { vertical: 'middle', horizontal: 'right' } };
  row.getCell('G').value = (fanData?.externalPressureDrop?.ra?.subTotal || 0).toFixed(1);
  row.getCell('G').style = { ...STYLES.TABLE_CELL_BOLD, alignment: { vertical: 'middle', horizontal: 'right' } };
  tempRow++;

  const externalEndRow = tempRow - 1;

  // ============================================
  // Supply Fan - 우측 컬럼 (H-M)
  // ============================================
  tempRow = internalStartRow;

  const supplyFanItems = [
    { label: 'Type', value: supplyFan?.type || '-' },
    { label: 'Model', value: supplyFan?.specification || '-' },
    {
      label: 'Air Vol.',
      value: (() => {
        const airflow = supplyFan?.airflow || 0;
        const ratio = fanData?.criteria?.saFanRatio || 1;
        const airflowPerUnit = Math.ceil(airflow / ratio);
        return airflow > 0 ? `${airflowPerUnit.toLocaleString()} ㎥/h x ${ratio}` : '-';
      })()
    },
    {
      label: 'Static P',
      value: (() => {
        const internalSubTotal = fanData?.internalPressureDrop?.subTotal || 0;
        const saSubTotal = fanData?.externalPressureDrop?.sa?.subTotal || 0;
        const spsTotal = Math.round((internalSubTotal + saSubTotal) * 10) / 10;
        return spsTotal > 0 ? `${Math.ceil(spsTotal)} Pa` : '-';
      })()
    },
    {
      label: 'Motor',
      value: (() => {
        const power = supplyFan?.power || 0;
        const ratio = fanData?.criteria?.saFanRatio || 1;
        return power > 0 ? `${power.toFixed(2)} kW x ${ratio}` : '-';
      })()
    },
    { label: 'Location', value: fanData?.criteria?.saFanPosition || '-' }
  ];

  supplyFanItems.forEach(item => {
    row = worksheet.getRow(tempRow);
    // 라벨을 H-I 2열로 병합
    worksheet.mergeCells(`H${tempRow}:I${tempRow}`);
    row.getCell('H').value = item.label;
    applyMergedCellStyle(worksheet, `H${tempRow}:I${tempRow}`, STYLES.TABLE_CELL);

    // 값을 J-M 4열로 병합
    worksheet.mergeCells(`J${tempRow}:M${tempRow}`);
    row.getCell('J').value = sanitizeCellValue(item.value);
    applyMergedCellStyle(worksheet, `J${tempRow}:M${tempRow}`, STYLES.TABLE_CELL);
    tempRow++;
  });

  // ============================================
  // Return Fan - 헤더 (H-M)
  // ============================================
  worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
  row = worksheet.getRow(tempRow);
  row.getCell('H').value = '■ Return Fan';
  applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });
  tempRow++;

  const returnFanItems = [
    { label: 'Type', value: returnFan?.type || '-' },
    { label: 'Model', value: returnFan?.specification || '-' },
    {
      label: 'Air Vol.',
      value: (() => {
        const airflow = returnFan?.airflow || 0;
        const ratio = fanData?.criteria?.raFanRatio || 1;
        const airflowPerUnit = Math.ceil(airflow / ratio);
        return airflow > 0 ? `${airflowPerUnit.toLocaleString()} ㎥/h x ${ratio}` : '-';
      })()
    },
    {
      label: 'Static P',
      value: (() => {
        const staticP = returnFan?.staticPressure || 0;
        return staticP > 0 ? `${Math.ceil(staticP)} Pa` : '-';
      })()
    },
    {
      label: 'Motor',
      value: (() => {
        const power = returnFan?.power || 0;
        const ratio = fanData?.criteria?.raFanRatio || 1;
        return power > 0 ? `${power.toFixed(2)} kW x ${ratio}` : '-';
      })()
    },
    { label: 'Location', value: fanData?.criteria?.raFanPosition || '-' }
  ];

  returnFanItems.forEach(item => {
    row = worksheet.getRow(tempRow);
    // 라벨을 H-I 2열로 병합
    worksheet.mergeCells(`H${tempRow}:I${tempRow}`);
    row.getCell('H').value = item.label;
    applyMergedCellStyle(worksheet, `H${tempRow}:I${tempRow}`, STYLES.TABLE_CELL);

    // 값을 J-M 4열로 병합
    worksheet.mergeCells(`J${tempRow}:M${tempRow}`);
    row.getCell('J').value = sanitizeCellValue(item.value);
    applyMergedCellStyle(worksheet, `J${tempRow}:M${tempRow}`, STYLES.TABLE_CELL);
    tempRow++;
  });

  // ============================================
  // Power Calculation - 헤더 (H-M)
  // ============================================
  worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
  row = worksheet.getRow(tempRow);
  row.getCell('H').value = '■ Power Calculation';
  applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });
  tempRow++;

  // Supply Fan Power
  const saAirflow = Math.round(supplyFan?.airflow || 0);
  const internalSubTotal = fanData?.internalPressureDrop?.subTotal || 0;
  const saSubTotal = fanData?.externalPressureDrop?.sa?.subTotal || 0;
  const spsTotal = Math.round((internalSubTotal + saSubTotal) * 10) / 10;
  const saStaticP = Math.ceil(spsTotal);
  const saEfficiency = (supplyFan?.efficiency || 65) / 100;
  const saSafetyFactor = (supplyFan?.safetyFactor || 15) / 100;
  const saPower = ((saAirflow * saStaticP) / (3600 * 1000 * saEfficiency) * (1 + saSafetyFactor));

  worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
  row = worksheet.getRow(tempRow);
  row.getCell('H').value = `Ps = ${saAirflow.toLocaleString()}㎥/h×${saStaticP}Pa÷(3600×1000×${saEfficiency})×${(1 + saSafetyFactor).toFixed(2)} = ${saPower.toFixed(2)} kW`;
  applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, STYLES.TABLE_CELL);
  tempRow++;

  // Return Fan Power (if exists)
  if (fanData?.criteria?.raFanPosition && fanData.criteria.raFanPosition !== 'None') {
    const raAirflow = Math.round(returnFan?.airflow || 0);
    const raStaticP = Math.ceil(returnFan?.staticPressure || 0);
    const raEfficiency = (returnFan?.efficiency || 50) / 100;
    const raSafetyFactor = (returnFan?.safetyFactor || 15) / 100;
    const raPower = ((raAirflow * raStaticP) / (3600 * 1000 * raEfficiency) * (1 + raSafetyFactor));

    worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
    row = worksheet.getRow(tempRow);
    row.getCell('H').value = `Pr = ${raAirflow.toLocaleString()}㎥/h×${raStaticP}Pa÷(3600×1000×${raEfficiency})×${(1 + raSafetyFactor).toFixed(2)} = ${raPower.toFixed(2)} kW`;
    applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, STYLES.TABLE_CELL);
    tempRow++;
  }

  // ============================================
  // Static Pressure - 헤더 (H-M)
  // ============================================
  worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
  row = worksheet.getRow(tempRow);
  row.getCell('H').value = '■ Static Pressure';
  applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, {
    font: { name: '맑은 고딕', size: 10, bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } } as ExcelJS.FillPattern,
    alignment: { vertical: 'middle', horizontal: 'left' },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    }
  });
  tempRow++;

  worksheet.mergeCells(`H${tempRow}:M${tempRow}`);
  row = worksheet.getRow(tempRow);
  row.getCell('H').value = `SPs = ${internalSubTotal.toFixed(1)} + ${saSubTotal.toFixed(1)} = ${spsTotal} Pa`;
  applyMergedCellStyle(worksheet, `H${tempRow}:M${tempRow}`, STYLES.TABLE_CELL);
  tempRow++;

  // 다음 섹션과의 간격을 위한 빈 행 1개 추가
  tempRow++;

  //console.log(`✅ FAN SELECTION 섹션 완료: ${ahu.ahuNo || ahu.id}`);

  // 다음 섹션을 위한 행 반환
  return tempRow;
};
